// src/components/AssessmentBriefDrawer.jsx
// Huginn-style brief drawer mounted on Builder + Explorer canvases/flows.
// Lets users attach reference documents (model cards, design docs, runbooks)
// as part of the assessment brief — supporting context, not the primary subject.
//
// Reuses the existing model-card upload pipeline; no new netlify functions.

import { useState, useEffect, useCallback } from 'react';
import { Drawer, Stack, Text, Alert } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { authFetch } from '../utils/authFetch';
import DocumentUpload from './DocumentUpload';

export default function AssessmentBriefDrawer({
  modelId,
  assessmentUuid,
  opened,
  onClose,
  documentType = 'context',
  bucket = 'model-cards',
}) {
  const [documents, setDocuments] = useState([]);

  const fetchDocuments = useCallback(async () => {
    if (!modelId) return;
    try {
      const params = new URLSearchParams({ modelId, documentType });
      if (assessmentUuid) params.set('assessmentUuid', assessmentUuid);
      const res = await authFetch(`/.netlify/functions/manageAssessmentDocuments?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setDocuments(Array.isArray(data?.documents) ? data.documents : []);
      }
    } catch (err) {
      console.warn('[AssessmentBriefDrawer] failed to fetch documents', err);
    }
  }, [modelId, assessmentUuid, documentType]);

  useEffect(() => {
    if (opened) fetchDocuments();
  }, [opened, fetchDocuments]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={<Text fw={600}>Brief</Text>}
      padding="lg"
    >
      <Stack gap="lg">
        <Alert color="blue" icon={<IconInfoCircle size={16} />} variant="light">
          <Text size="sm">
            These documents are part of the assessment <strong>brief</strong> — supporting
            context, not the primary subject. Upload model cards, design docs, runbooks,
            regulatory filings, or anything else that helps the agency understand the situation.
          </Text>
        </Alert>

        <DocumentUpload
          modelId={modelId}
          documentType={documentType}
          bucket={bucket}
          label="Supporting documents"
          description="PDF, DOC, DOCX, TXT, MD"
          acceptedTypes=".pdf,.doc,.docx,.txt,.md"
          existingDocuments={documents}
          onUploadComplete={(doc) => setDocuments((prev) => [...prev, doc])}
          onDocumentDeleted={(id) => setDocuments((prev) => prev.filter((d) => d.id !== id))}
          compact
        />
      </Stack>
    </Drawer>
  );
}
